import {  useEffect, useState } from "react";
import CONFIG from "../config";
import type { ItemReponse, LoginResponse, ServerReponse } from "../types/CommonReponse";
import type { AuthUser } from "../types/Users";

interface FetchQueryState<T> {
    isSuccess: boolean;
    isLoading: boolean;
    error: string | null | Record<string, string>  ;
    data: T | null;
    status:number;
}

interface LostPasswordRequestHook {
    result: FetchQueryState<boolean>;
    lostPasswordRequest: (user:string)=>void;
}


interface ResetPasswordRequestHook {
    result: FetchQueryState<boolean>;
    resetPasswordRequest: (token:string,newpassword:string)=>void;
}
interface ResetPasswordRequestHookParam {
    token:string;
    newpassword:string;
}
interface RegisterRequestHook {
    result: FetchQueryState<boolean>;
    registerRequest: (user:string,password:string,email:string,reglement:number | undefined)=>void;
}

export interface RegisterRequestHookParam {
    identifiant:string;
    password:string;
    email:string;
    reglement:number | undefined
}


export  function  Login(user: string, password: string): Promise<AuthUser|null> {
    return fetch(CONFIG.SERVER + '/login',{
            method: "POST", 
            credentials: 'include',
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ user: user, password: password }),
        })
        .then(response=>{
            if(response.ok){
                return response.json()
            }else{
                throw Error();
            }
        })
        .then((data:LoginResponse)=>{
            if(data.data){
                const auth: AuthUser =  {
                    id: data.data.id,
                    identifiant: data.data.identifiant,
                    cookiehash: data.data.cookiehash,
                    email: data.data.email,
                    rangacces: data.data.rangacces,
                    valide:data.data.validation
                };

                return auth;
            }  
            else{
                return null;
            }      
        }).catch(()=>{
            return null;
        });
}
export  function  Logout(): Promise<boolean> {
    return fetch(CONFIG.SERVER + '/login',{
        method: "DELETE", 
        credentials: 'include',
        headers: {
            "Content-Type": "application/json",
        },
     }).then(response=>{
        return response.ok;       
    });
}

export  function isLoggedIn(token:string|null|undefined): Promise<AuthUser|null> {

    const params:RequestInit=
        {
            credentials: 'include'
        };
    if(token){
        params.headers=[['Authorization', 'Token '+token]];
    }
    return fetch(CONFIG.SERVER + '/login',params)
    .then(response=>{
        if(response.ok){
            return response.json();
        }else{
            throw Error();
        }
    })
    .then((data:LoginResponse)=>{
        if(data.data){
            const auth: AuthUser =  {
                id: data.data.id,
                identifiant: data.data.identifiant,
                cookiehash: data.data.cookiehash,
                email: data.data.email,
                rangacces: data.data.rangacces
            };

            return auth;
        } else{
            return null;
        }   

    }).catch(function(){
        return null;
    });
}

export function useLostPasswordRequest(): LostPasswordRequestHook {
    const [state,setState]=useState<FetchQueryState<boolean>>({
            isSuccess:false,
            isLoading:false,
            error:null,
            data:null,
            status:0,
        });
    const [user,setUser]=useState(null as string|null);
    useEffect(()=>{
        if(user===null){
            return;
        }
        setState({
            isSuccess:false,
            isLoading:true,
            error:null,
            data:null,
            status:0,
        });

        fetch(CONFIG.SERVER + '/login/lost',{
                method: "POST", 
                credentials: 'include',
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ user: user }),
            }
        )
        .then(response=>{
            return response.json().then((data:ServerReponse<null>)=>{
                const payload:ItemReponse<null>={
                    success:response.ok,status:response.status,...data}
                return payload;
            });
        })
        .then((data:ItemReponse<null>)=>{
            setState({
                isSuccess:data.success,
                isLoading:false,
                error:"",
                data:null,
                status:data.status??0
            });
        }).catch((err:unknown)=>{

            setState({
                isSuccess:false,
                isLoading:false,
                error:err as string,
                data:null,
                status:0
            });


            
        });

    },[user]);
    function lostPasswordRequest(user:string){
        setUser(user);
    }
    return {
        result:state,
        lostPasswordRequest:lostPasswordRequest
    };  
}

export function useResetPasswordRequest(): ResetPasswordRequestHook {
    const [state,setState]=useState<FetchQueryState<boolean>>({
            isSuccess:false,
            isLoading:false,
            error:null,
            data:null,
            status:0
        });
    const [param,setParam]=useState<ResetPasswordRequestHookParam|null>(null);
    useEffect(()=>{
        if(!param){
            return;
        }
        setState({
            isSuccess:false,
            isLoading:true,
            error:null,
            data:null,
            status:0
        });
        fetch(CONFIG.SERVER + '/login/reset',{
                method: "POST", 
                credentials: 'include',
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ token: param.token, newpassword: param.newpassword }),
            }
        )
        .then(response=>{            
            return response.json().then((data:ServerReponse<null>)=>{
                const payload:ItemReponse<null>={success:response.ok,status:response.status,...data}
                return payload;
            });
        })
        .then((data:ItemReponse<null>)=>{
            setState({
                isSuccess:data.success,
                isLoading:false,
                error:data.error??null,
                data:null,
                status:data.status??0
            });
        }).catch((err:unknown)=>{
            setState({
                isSuccess:false,
                isLoading:false,
                error:err as string,
                data:null,
                status:0
            });
        });
    },[param]);
    function resetPasswordRequest(token:string,newpassword:string){
        setParam({token:token,newpassword:newpassword});
    }
    return {
        result:state,
        resetPasswordRequest:resetPasswordRequest
    };
}

export function useRegisterRequest(): RegisterRequestHook {
    const [state,setState]=useState<FetchQueryState<boolean>>({
            isSuccess:false,
            isLoading:false,
            error:null,
            data:null,
            status:0
        });
    const [param,setParam]=useState<RegisterRequestHookParam|null>(null);
    useEffect(()=>{
        if(!param){
            return;
        }
        setState({
            isSuccess:false,
            isLoading:true,
            error:null,
            data:null,
            status:0
        });
        fetch(CONFIG.SERVER + '/login/register',{
                method: "POST", 
                credentials: 'include',
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ 
                    identifiant: param.identifiant, 
                    password: param.password, 
                    email: param.email,
                    reglement:param.reglement
                }),
            }
        )
        .then(response=>{            
            return response.json().then((data:ServerReponse<null>)=>{
                const payload:ItemReponse<null>={success:response.ok,status:response.status,...data}
                return payload;
            });
        })
        .then((data:ItemReponse<null>)=>{
            setState({
                isSuccess:data.success,
                isLoading:false,
                error:data.error??null,
                data:null,
                status:data.status??0
            });
        }).catch((err:unknown)=>{
            setState({
                isSuccess:false,
                isLoading:false,
                error:err as string,
                data:null,
                status:0
            });
        });
    },[param]);
    function registerRequest(identifiant:string,password:string,email:string,reglement:number|undefined){
        setParam({identifiant:identifiant,password:password,email:email,reglement:reglement});
    }
    return {
        result:state,
        registerRequest:registerRequest
    };
}


